import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, PencilIcon, PlusIcon, UsersIcon, UsersRoundIcon, XIcon } from 'lucide-react';
import { Button, Combobox, ConfirmDialog, DataTable, EmptyState, ErrorNotice, FactList, Field, LoadingState, MultiCombobox, Panel, Pill, SearchInput, SelectInput, TextArea, TextInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { cn } from '@/lib/utils';
import {
  ASSISTANT_ROLES,
  ASSISTANT_ROLE_LABELS,
  ASSISTANT_STATUSES,
  MAX_BULK_ASSISTANTS,
  PAY_BASIS,
  PAY_BASIS_LABELS,
  STAFF_SOURCES,
  STAFF_SOURCE_LABELS,
  addAssistant,
  assistantStatusTone,
  bulkAddAssistants,
  categoryFill,
  formatDollars,
  formatPay,
  createStaffContact,
  listAssistants,
  listPeople,
  removeAssistant,
  saveStaffing,
  similarStaffContacts,
  updateAssistant,
  type AssistantRole,
  type AssistantStatus,
  type AssistantWrite,
  type AssistantsView,
  type CategoryPayTotal,
  type EventAssistant,
  type EventCategoryOption,
  type PayBasis,
  type PersonRef,
  type SimilarPerson,
  type StaffSource,
  type Staffing,
} from '@/lib/staffing';

const SORT_FIELDS = { name: 'name', role: 'roleLabel', start: 'callTime', status: 'statusOrder' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };
const STATUS_ORDER = { requested: 0, confirmed: 1, canceled: 2 } as const;

interface AssistantRow extends EventAssistant {
  readonly name: string;
  readonly roleLabel: string;
  readonly categoryName: string;
  readonly statusOrder: number;
  readonly contactText: string;
  readonly hiredByLabel: string;
}
function assistantRow(row: EventAssistant, categoryNames: ReadonlyMap<string, string>): AssistantRow {
  return {
    ...row,
    name: row.contact?.name ?? 'Unknown',
    roleLabel: ASSISTANT_ROLE_LABELS[row.role] ?? row.role,
    categoryName: row.categoryId ? (categoryNames.get(row.categoryId) ?? '') : '',
    statusOrder: STATUS_ORDER[row.status],
    contactText: [row.contact?.email, row.contact?.phone].filter(Boolean).join(' '),
    hiredByLabel: row.hiredBy ? STAFF_SOURCE_LABELS[row.hiredBy] : '',
  };
}

/** The row fields the search box matches. */
const ASSISTANT_SEARCH_FIELDS: ReadonlyArray<keyof AssistantRow> = ['name', 'roleLabel', 'categoryName', 'contactText', 'hiredByLabel', 'status', 'note'];

/** Its own URL key: the event page has other searchable lists. */
const ASSISTANT_SEARCH_KEY = 'assistantsQ';

/**
 * Assistants on the event: who, in what role, call time, parking, who
 * hired them, and (internal only) pay. Everyone on the event adds;
 * facilitators see names, roles, times and parking.
 */
export function AssistantsPanel({ eventId, isInternal }: { readonly eventId: string; readonly isInternal: boolean }): React.ReactElement {
  const queryClient = useQueryClient();
  const queryKey = ['assistants', eventId];
  const [adding, setAdding] = useState(false);
  const [addingSeveral, setAddingSeveral] = useState(false);
  const [editing, setEditing] = useState<EventAssistant | null>(null);
  const categoriesOn = useAppFeatures()(APP_FEATURE.assistantCategories);
  const [planning, setPlanning] = useState(false);
  const [removing, setRemoving] = useState<EventAssistant | null>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch(ASSISTANT_SEARCH_KEY);
  const view = useQuery({ queryKey, queryFn: ({ signal }) => listAssistants(eventId, signal) });
  const apply = (next: AssistantsView): void => {
    queryClient.setQueryData(queryKey, next);
  };
  const remove = useMutation({
    mutationFn: (row: EventAssistant) => removeAssistant(eventId, row._id),
    onSuccess: (next, row) => {
      apply(next);
      setRemoving(null);
      toast.success(`Removed ${row.contact?.name ?? 'assistant'}`);
    },
  });
  const setStatus = useMutation({
    mutationFn: (input: { row: EventAssistant; status: AssistantStatus }) => updateAssistant(eventId, input.row._id, { status: input.status }),
    onSuccess: apply,
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (view.isPending) return <LoadingState label="Loading assistants…" />;
  if (view.isError) return <ErrorNotice title="Assistants unavailable" message={errorMessage(view.error)} />;
  const { assistants, staffing } = view.data;
  // Switched off, categories leave every screen; hires keep their categoryId for when it comes back.
  const categories = categoriesOn ? (view.data.categories ?? []) : [];
  const pickable = categories.filter((category) => !category.deleted);
  const categoryNames = new Map(categories.map((category) => [category._id, category.deleted ? `${category.name} (deleted)` : category.name]));
  const rows = sortRows(filterRows(assistants.map((row) => assistantRow(row, categoryNames)), search, ASSISTANT_SEARCH_FIELDS), sort);
  const onEvent = new Set(assistants.filter((row) => row.status !== 'canceled').map((row) => (row.userId ? `user:${row.userId}` : `contact:${row.staffContactId ?? ''}`)));
  const confirmed = assistants.filter((row) => row.status === 'confirmed').length;
  const plan = [
    staffing.hiringMode ? `Hired by ${staffing.hiringMode === 'mixed' ? 'facilitator and WingTeam' : STAFF_SOURCE_LABELS[staffing.hiringMode]}` : null,
    staffing.requestedCount !== null && staffing.requestedCount !== undefined ? `${staffing.requestedCount} requested` : null,
    `${confirmed} confirmed`,
  ].filter(Boolean).join(' · ');

  return (
    <Panel className="p-5">
      <div data-guide="assistants-header" className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-portal-card-title font-bold">Assistants</h2>
          <p className="text-portal-micro text-portal-muted">
            {plan}
            {staffing.notes ? ` · ${staffing.notes}` : ''}
          </p>
        </div>
        <span className="flex items-center gap-2">
          {isInternal ? (
            <span data-guide="assistants-plan">
              <Button variant="secondary" size="compact" onClick={() => setPlanning(true)}>
                <PencilIcon aria-hidden className="size-4" /> Staffing plan
              </Button>
            </span>
          ) : null}
          {categoriesOn ? (
            <span data-guide="assistants-add-several">
              <Button variant="secondary" size="compact" onClick={() => setAddingSeveral(true)}>
                <UsersRoundIcon aria-hidden className="size-4" /> Add several
              </Button>
            </span>
          ) : null}
          <span data-guide="assistants-add">
            <Button size="compact" onClick={() => setAdding(true)}>
              <PlusIcon aria-hidden className="size-4" /> Add assistant
            </Button>
          </span>
        </span>
      </div>
      {assistants.length > 0 ? (
        <SearchInput
          label="Search assistants"
          placeholder={categoriesOn ? 'Name, role, category, email, phone, hired by or status' : 'Name, role, email, phone, hired by or status'}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="mb-3 w-full max-w-sm"
        />
      ) : null}
      {assistants.length === 0 ? (
        <EmptyState icon={<UsersIcon aria-hidden className="size-6" />} title="No assistants yet" message="Add who is working the event: anyone on the BCE team, a facilitator, or a staff contact. Existing people are found as you type, so nobody is entered twice." action={<Button onClick={() => setAdding(true)}><PlusIcon aria-hidden className="size-4" /> Add assistant</Button>} />
      ) : rows.length === 0 ? (
        <EmptyState title="No assistants match" message="Try a different search." />
      ) : (
        <div data-guide="assistants-table"><DataTable
          headers={[{ label: 'Name', sortKey: SORT_FIELDS.name }, { label: 'Role', sortKey: SORT_FIELDS.role }, { label: 'Start', sortKey: SORT_FIELDS.start }, ...(isInternal ? ['Pay'] : []), 'Hired by', { label: 'Status', sortKey: SORT_FIELDS.status }, 'Actions']}
          visuallyHiddenHeaders={[isInternal ? 6 : 5]}
          sort={sort}
          onSort={onSort}
          flush
          minWidth={760}
        >
          {rows.map((row) => (
            <tr key={row._id} className={cn('border-t border-portal-line align-middle', row.status === 'canceled' && 'bg-portal-bg/60')}>
              <td className="p-3 text-portal-small">
                <Link to={row.userId ? `/people/${row.userId}` : `/staff-contacts/${row.staffContactId ?? ''}`} className="block font-bold text-brand hover:underline">
                  {row.name}
                </Link>
                <span className="block text-portal-micro text-portal-muted">{[row.contact?.phone, row.contact?.city].filter(Boolean).join(' · ')}</span>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">
                <span className="block">{row.roleLabel}</span>
                {row.categoryName ? <span className="block text-portal-micro">{row.categoryName}</span> : null}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                {row.callTime ? `${row.callTime}${row.endTime ? ` – ${row.endTime}` : ''}` : '—'}
                {row.parkingNeeded ? <span className="block text-portal-micro text-portal-muted">Parking needed</span> : null}
              </td>
              {isInternal ? (
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap tabular-nums">
                  {formatPay(row.payRate, row.payBasis) ?? '—'}
                </td>
              ) : null}
              <td className="p-3 text-portal-small text-portal-muted">{row.hiredBy ? STAFF_SOURCE_LABELS[row.hiredBy] : '—'}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={assistantStatusTone(row.status)}>{row.status[0]!.toUpperCase() + row.status.slice(1)}</Pill>
              </td>
              <td className="p-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-guide="assistants-row-menu" aria-label={`Actions for ${row.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                      <EllipsisIcon aria-hidden className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing(row)}>
                      <PencilIcon aria-hidden className="size-4" /> Edit
                    </DropdownMenuItem>
                    {ASSISTANT_STATUSES.filter((status) => status !== row.status).map((status) => (
                      <DropdownMenuItem key={status} onSelect={() => setStatus.mutate({ row, status })}>
                        Mark {status}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(row)}>
                      <XIcon aria-hidden className="size-4" /> Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </DataTable></div>
      )}
      {isInternal && categoriesOn && assistants.length > 0 && view.data.payTotals && view.data.payTotals.length > 0 ? <PayTotals totals={view.data.payTotals} categoryNames={categoryNames} /> : null}
      {adding ? <AssistantDialog eventId={eventId} isInternal={isInternal} categories={pickable} onClose={() => setAdding(false)} onSaved={(next) => { apply(next); setAdding(false); }} /> : null}
      {editing ? <AssistantDialog key={editing._id} eventId={eventId} isInternal={isInternal} categories={pickable} existing={editing} onClose={() => setEditing(null)} onSaved={(next) => { apply(next); setEditing(null); }} /> : null}
      {addingSeveral ? <BulkAssistantsDialog eventId={eventId} isInternal={isInternal} categories={pickable} onEvent={onEvent} onClose={() => setAddingSeveral(false)} onSaved={(next) => { apply(next); setAddingSeveral(false); }} /> : null}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing?.contact?.name ?? 'this assistant'} from the event?`}
        message="They stay in the staff directory; only this event's hire goes."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
      {planning ? (
        <StaffingDialog
          eventId={eventId}
          existing={staffing}
          onClose={() => setPlanning(false)}
          onSaved={async () => {
            setPlanning(false);
            await queryClient.invalidateQueries({ queryKey });
          }}
        />
      ) : null}
    </Panel>
  );
}

/** How a person is chosen for a hire: from the directory and team, or created on the spot. */
const PERSON_MODE = { existing: 'existing', create: 'create' } as const;
type PersonMode = (typeof PERSON_MODE)[keyof typeof PERSON_MODE];

/** "Dana Whitfield · Staff contact": how a person reads in the picker. */
function personKindLabel(person: Pick<SimilarPerson, 'kind' | 'role'>): string {
  if (person.kind === 'user') return person.role === 'contractor' ? 'Facilitator' : 'BCE team';
  return 'Staff contact';
}

/** The picker's value for a person: kind and id, since a user and a contact can share neither. */
function personValue(person: Pick<SimilarPerson, 'kind' | '_id'>): string {
  return `${person.kind}:${person._id}`;
}

/** What a category gives each person, in one line: "Assistant · $30/hr · 2:00 PM to 5:30 PM". */
function categorySummary(category: EventCategoryOption, isInternal: boolean): string {
  const times = category.callTime || category.endTime ? `${category.callTime ?? 'start not set'} to ${category.endTime ?? 'end not set'}` : 'times not set';
  const pay = isInternal ? (formatPay(category.payRate, category.payBasis) ?? 'pay not set') : null;
  return [ASSISTANT_ROLE_LABELS[category.role], pay, times].filter(Boolean).join(' · ');
}

/** The category picker both dialogs share. */
function CategoryField({ id, categories, value, isInternal, onChange }: { readonly id: string; readonly categories: readonly EventCategoryOption[]; readonly value: string; readonly isInternal: boolean; readonly onChange: (category: EventCategoryOption | null) => void }): React.ReactElement {
  const chosen = categories.find((category) => category._id === value);
  return (
    <Field guide="assistant-category" label="Category" htmlFor={id} hint={chosen ? `Fills in ${categorySummary(chosen, isInternal)}. Change any of it for this person.` : 'Fills in the standard role, pay and hours. Set up in Settings.'}>
      <SelectInput id={id} value={value} onChange={(event) => onChange(categories.find((category) => category._id === event.target.value) ?? null)}>
        <option value="">No category</option>
        {categories.map((category) => (
          <option key={category._id} value={category._id}>
            {category.name}
          </option>
        ))}
      </SelectInput>
    </Field>
  );
}

/**
 * Add or edit one hire. The person is picked from everyone who can work an
 * event; only when they are not there does "Add a new person" open the
 * fields for someone new. Before a new person is saved, anyone with the same
 * name, phone or email is offered instead, so nobody is entered twice.
 */
function AssistantDialog({ eventId, isInternal, categories, existing, onClose, onSaved }: { readonly eventId: string; readonly isInternal: boolean; readonly categories: readonly EventCategoryOption[]; readonly existing?: EventAssistant; readonly onClose: () => void; readonly onSaved: (next: AssistantsView) => void }): React.ReactElement {
  const [mode, setMode] = useState<PersonMode>(PERSON_MODE.existing);
  const [person, setPerson] = useState<{ _id: string; name: string; kind: 'contact' | 'user' } | null>(
    existing?.contact ? { _id: existing.contact._id, name: existing.contact.name, kind: existing.contact.kind ?? 'contact' } : null,
  );
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [lookalikes, setLookalikes] = useState<readonly SimilarPerson[] | null>(null);
  const [choice, setChoice] = useState<string>('new');
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? '');
  const [role, setRole] = useState<AssistantRole>(existing?.role ?? 'assistant');
  const [callTime, setCallTime] = useState(existing?.callTime ?? '');
  const [endTime, setEndTime] = useState(existing?.endTime ?? '');
  const [payRate, setPayRate] = useState(existing?.payRate !== null && existing?.payRate !== undefined ? String(existing.payRate) : '');
  const [payBasis, setPayBasis] = useState<PayBasis>(existing?.payBasis ?? PAY_BASIS.flat);
  const [parkingNeeded, setParkingNeeded] = useState(existing?.parkingNeeded ?? false);
  const [hiredBy, setHiredBy] = useState<StaffSource | ''>(existing?.hiredBy ?? '');
  const [status, setStatus] = useState<AssistantStatus>(existing?.status ?? 'requested');
  const [note, setNote] = useState(existing?.note ?? '');
  const people = useQuery({ queryKey: ['people'], queryFn: ({ signal }) => listPeople(signal), staleTime: 60_000 });
  const personOptions = (people.data ?? []).map((candidate) => ({ value: personValue(candidate), label: `${candidate.name} · ${personKindLabel(candidate)}` }));
  // Sent only when it changed, so a deleted category stays on the hire it was given to but is never offered again.
  const categoryChanged = categoryId !== (existing?.categoryId ?? '');
  const categoryShown = categories.some((category) => category._id === categoryId) ? categoryId : '';

  /** Someone new: the fields open with what was typed in the search. */
  function startNewPerson(typed: string): void {
    setMode(PERSON_MODE.create);
    setName(typed);
    setLookalikes(null);
  }

  function pickCategory(category: EventCategoryOption | null): void {
    setCategoryId(category?._id ?? '');
    if (!category) return;
    const fill = categoryFill(category);
    setRole(fill.role);
    setCallTime(fill.callTime);
    setEndTime(fill.endTime);
    if (fill.payRate !== undefined) setPayRate(fill.payRate);
    if (fill.payBasis) setPayBasis(fill.payBasis);
  }

  const hireBody = (chosen: { _id: string; kind: 'contact' | 'user' }): AssistantWrite => ({
    ...(chosen.kind === 'user' ? { userId: chosen._id } : { staffContactId: chosen._id }),
    // Picking "No category" on an edit takes the category off.
    ...(categoryChanged ? { categoryId: categoryId || null } : {}),
    role,
    callTime: callTime.trim(),
    endTime: endTime.trim(),
    ...(isInternal ? { payRate: payRate.trim() === '' ? null : Number(payRate), payBasis } : {}),
    parkingNeeded,
    hiredBy: hiredBy || undefined,
    status,
    note: note.trim(),
  });

  const save = useMutation({
    mutationFn: async (input: { readonly createAnyway: boolean; readonly usePerson?: { _id: string; name: string; kind: 'contact' | 'user' } }): Promise<{ lookalikes: readonly SimilarPerson[] } | { next: AssistantsView; chosenName: string }> => {
      let chosen = input.usePerson ?? person;
      if (!input.usePerson && mode === PERSON_MODE.create) {
        if (!input.createAnyway) {
          // The guard: anyone with this name, phone or email is offered before a new record is made.
          const found = await similarStaffContacts({ name: name.trim(), phone: phone.trim(), email: email.trim() });
          if (found.length > 0) return { lookalikes: found };
        }
        const created = await createStaffContact({ name: name.trim(), phone: phone.trim(), email: email.trim(), source: hiredBy || 'facilitator' });
        chosen = { _id: created._id, name: created.name, kind: 'contact' };
      }
      if (!chosen) throw new Error('Pick a person first.');
      const next = existing ? await updateAssistant(eventId, existing._id, hireBody(chosen)) : await addAssistant(eventId, hireBody(chosen));
      return { next, chosenName: chosen.name };
    },
    onSuccess: (result) => {
      if ('lookalikes' in result) {
        setLookalikes(result.lookalikes);
        const first = result.lookalikes[0];
        setChoice(first ? personValue(first) : 'new');
        return;
      }
      toast.success(existing ? 'Assistant saved' : `Added ${result.chosenName}`);
      onSaved(result.next);
    },
  });

  const personReady = mode === PERSON_MODE.existing ? person !== null : name.trim().length > 1;
  const valid = personReady && !save.isPending;

  if (lookalikes && lookalikes.length > 0) {
    const count = lookalikes.length;
    const chosenMatch = lookalikes.find((match) => personValue(match) === choice);
    return (
      <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
        <DialogContent data-guide="assistant-lookalikes" className="sm:max-w-lg">
          <DialogTitle>We found {count} {count === 1 ? 'person' : 'people'} like this</DialogTitle>
          <p className="text-portal-small text-portal-muted">Use one of them, or keep what you entered.</p>
          <fieldset className="mt-4 space-y-3">
            <legend className="sr-only">Which person</legend>
            <div>
              <span className="mb-1.5 block text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">We found</span>
              <ul className="divide-y divide-portal-line rounded-portal-button border border-portal-line">
                {lookalikes.slice(0, 6).map((match) => {
                  const value = personValue(match);
                  return (
                    <li key={value}>
                      <label className={cn('flex cursor-pointer items-start gap-3 px-3 py-2.5 text-portal-small', choice === value && 'bg-portal-sky')}>
                        <input type="radio" name="assistant-person-choice" value={value} checked={choice === value} onChange={() => setChoice(value)} className="mt-1" />
                        <span className="min-w-0">
                          <span className="block font-bold">{match.name}</span>
                          <span className="block text-portal-micro text-portal-muted">{[personKindLabel(match), match.city, match.phone, match.email].filter(Boolean).join(' · ')}</span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <span className="mb-1.5 block text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">You entered</span>
              <label className={cn('flex cursor-pointer items-start gap-3 rounded-portal-button border border-portal-line px-3 py-2.5 text-portal-small', choice === 'new' && 'bg-portal-sky')}>
                <input type="radio" name="assistant-person-choice" value="new" checked={choice === 'new'} onChange={() => setChoice('new')} className="mt-1" />
                <span className="min-w-0">
                  <span className="block font-bold">{name.trim()}</span>
                  <span className="block text-portal-micro text-portal-muted">{['New person', phone.trim(), email.trim()].filter(Boolean).join(' · ')}</span>
                </span>
              </label>
            </div>
          </fieldset>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="mt-4 flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setLookalikes(null)} disabled={save.isPending}>
              Back
            </Button>
            <Button
              type="button"
              disabled={save.isPending}
              onClick={() => {
                if (chosenMatch) {
                  setPerson({ _id: chosenMatch._id, name: chosenMatch.name, kind: chosenMatch.kind });
                  setMode(PERSON_MODE.existing);
                  setLookalikes(null);
                  save.mutate({ createAnyway: false, usePerson: { _id: chosenMatch._id, name: chosenMatch.name, kind: chosenMatch.kind } });
                } else {
                  save.mutate({ createAnyway: true });
                }
              }}
            >
              {save.isPending ? 'Saving…' : 'Continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent data-guide="assistant-dialog" className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>{existing ? 'Edit assistant' : 'Add assistant'}</DialogTitle>
        <form className="mt-2 space-y-4" onSubmit={(event) => { event.preventDefault(); if (valid) save.mutate({ createAnyway: false }); }}>
          {mode === PERSON_MODE.existing ? (
            <div>
              <Field guide="assistant-person" label="Person" htmlFor="assistant-person" required hint="Type a name to search the BCE team, the facilitators and the staff directory.">
                <Combobox
                  id="assistant-person"
                  options={personOptions}
                  value={person ? personValue(person) : ''}
                  onChange={(value) => {
                    const match = (people.data ?? []).find((candidate) => personValue(candidate) === value);
                    setPerson(match ? { _id: match._id, name: match.name, kind: match.kind } : null);
                  }}
                  placeholder="Search by name"
                  noneLabel="Nobody yet"
                  createOption={{ label: (typed) => (typed ? `Add “${typed}” as a new person` : 'Add a new person'), onSelect: startNewPerson }}
                />
              </Field>
              <button type="button" data-guide="assistant-new" onClick={() => startNewPerson('')} className="mt-1.5 text-portal-micro font-bold text-brand hover:underline">
                Not in the list? Add a new person
              </button>
            </div>
          ) : (
            <div className="space-y-4 rounded-portal-button border border-portal-line p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-portal-small font-bold">New person</span>
                <button type="button" onClick={() => { setMode(PERSON_MODE.existing); setLookalikes(null); }} className="text-portal-micro font-bold text-brand hover:underline">
                  Search the directory instead
                </button>
              </div>
              <Field guide="assistant-name" label="Name" htmlFor="assistant-name" required hint="First and last name. They join the staff directory.">
                <TextInput id="assistant-name" value={name} maxLength={120} placeholder="Dana Whitfield" autoFocus onChange={(event) => { setName(event.target.value); setLookalikes(null); }} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field guide="assistant-phone" label="Phone" htmlFor="assistant-phone" hint="Used to spot the same person twice.">
                  <TextInput id="assistant-phone" type="tel" value={phone} onChange={(event) => { setPhone(event.target.value); setLookalikes(null); }} />
                </Field>
                <Field guide="assistant-email" label="Email" htmlFor="assistant-email" hint="Used to spot the same person twice.">
                  <TextInput id="assistant-email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setLookalikes(null); }} />
                </Field>
              </div>
            </div>
          )}
          {categories.length > 0 ? <CategoryField id="assistant-category" categories={categories} value={categoryShown} isInternal={isInternal} onChange={pickCategory} /> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="assistant-role" label="Role" htmlFor="assistant-role" required hint="What they do on the day.">
              <SelectInput id="assistant-role" value={role} onChange={(event) => setRole(event.target.value as AssistantRole)}>
                {ASSISTANT_ROLES.map((key) => (
                  <option key={key} value={key}>
                    {ASSISTANT_ROLE_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="assistant-hired-by" label="Hired by" htmlFor="assistant-hired-by" hint="Who brought them on.">
              <SelectInput id="assistant-hired-by" value={hiredBy} onChange={(event) => setHiredBy(event.target.value as StaffSource | '')}>
                <option value="">Not said</option>
                {STAFF_SOURCES.map((key) => (
                  <option key={key} value={key}>
                    {STAFF_SOURCE_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="assistant-start" label="Start time" htmlFor="assistant-call" hint="When they start.">
              <TextInput id="assistant-call" value={callTime} placeholder="12:30 PM" onChange={(event) => setCallTime(event.target.value)} />
            </Field>
            <Field guide="assistant-end" label="End time" htmlFor="assistant-end" hint="When they are done.">
              <TextInput id="assistant-end" value={endTime} placeholder="5:00 PM" onChange={(event) => setEndTime(event.target.value)} />
            </Field>
            {isInternal ? (
              <>
                <Field guide="assistant-pay" label="Pay" htmlFor="assistant-pay" hint="Dollars; internal only, never shown to facilitators.">
                  <TextInput id="assistant-pay" type="number" min={0} step="0.01" value={payRate} placeholder="25" onChange={(event) => setPayRate(event.target.value)} />
                </Field>
                <Field guide="assistant-pay-basis" label="Pay basis" htmlFor="assistant-pay-basis" hint="Per hour or one flat amount.">
                  <SelectInput id="assistant-pay-basis" value={payBasis} onChange={(event) => setPayBasis(event.target.value as PayBasis)}>
                    {Object.values(PAY_BASIS).map((key) => (
                      <option key={key} value={key}>
                        {PAY_BASIS_LABELS[key]}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
              </>
            ) : null}
            <Field guide="assistant-status" label="Status" htmlFor="assistant-status" required hint="Requested until they confirm.">
              <SelectInput id="assistant-status" value={status} onChange={(event) => setStatus(event.target.value as AssistantStatus)}>
                {ASSISTANT_STATUSES.map((key) => (
                  <option key={key} value={key}>
                    {key[0]!.toUpperCase() + key.slice(1)}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <label data-guide="assistant-parking" className="flex items-center gap-2 self-end pb-2 text-portal-small">
              <input type="checkbox" checked={parkingNeeded} onChange={(event) => setParkingNeeded(event.target.checked)} />
              Needs parking
            </label>
          </div>
          <Field guide="assistant-note" label="Note" htmlFor="assistant-note" hint="Anything the day needs from them.">
            <TextArea id="assistant-note" rows={2} value={note} maxLength={1000} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {!isInternal ? <p className="text-portal-micro text-portal-muted">Pay is set by BCE and not shown here.</p> : null}
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button guide="assistant-submit" type="submit" disabled={!valid}>{save.isPending ? 'Saving…' : existing ? 'Save' : 'Add assistant'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Adds a whole team at once, for payroll on a large event: pick a category
 * and everyone from the directory, and each gets the category's role, pay and
 * hours. Anyone already on the event is left out of the picker; someone new
 * is added one at a time with Add assistant.
 */
function BulkAssistantsDialog({ eventId, isInternal, categories, onEvent, onClose, onSaved }: { readonly eventId: string; readonly isInternal: boolean; readonly categories: readonly EventCategoryOption[]; readonly onEvent: ReadonlySet<string>; readonly onClose: () => void; readonly onSaved: (next: AssistantsView) => void }): React.ReactElement {
  const [categoryId, setCategoryId] = useState(categories.length === 1 ? (categories[0]?._id ?? '') : '');
  const [role, setRole] = useState<AssistantRole>('assistant');
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [hiredBy, setHiredBy] = useState<StaffSource | ''>('');
  const [status, setStatus] = useState<AssistantStatus>('requested');
  const people = useQuery({ queryKey: ['people'], queryFn: ({ signal }) => listPeople(signal), staleTime: 60_000 });
  const options = (people.data ?? [])
    .filter((candidate) => !onEvent.has(personValue(candidate)))
    .map((candidate) => ({ value: personValue(candidate), label: `${candidate.name} · ${personKindLabel(candidate)}` }));
  const category = categories.find((candidate) => candidate._id === categoryId);

  const save = useMutation({
    mutationFn: () => {
      const refs = picked.flatMap((value): PersonRef[] => {
        const [kind, id] = value.split(':');
        if (!id) return [];
        return kind === 'user' ? [{ userId: id }] : [{ staffContactId: id }];
      });
      return bulkAddAssistants(eventId, {
        people: refs,
        ...(category ? { categoryId: category._id } : { role }),
        ...(hiredBy ? { hiredBy } : {}),
        status,
      });
    },
    onSuccess: (result) => {
      const added = `Added ${result.added} ${result.added === 1 ? 'assistant' : 'assistants'}`;
      toast.success(result.skipped > 0 ? `${added}. ${result.skipped} already on the event.` : added);
      onSaved(result);
    },
  });
  const tooMany = picked.length > MAX_BULK_ASSISTANTS;
  const valid = picked.length > 0 && !tooMany && !save.isPending;

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent data-guide="assistants-bulk-dialog" className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>Add several assistants</DialogTitle>
        <form className="mt-2 space-y-4" onSubmit={(event) => { event.preventDefault(); if (valid) save.mutate(); }}>
          {categories.length > 0 ? (
            <CategoryField id="bulk-category" categories={categories} value={categoryId} isInternal={isInternal} onChange={(next) => setCategoryId(next?._id ?? '')} />
          ) : null}
          {category ? null : (
            <Field label="Role" htmlFor="bulk-role" required hint={categories.length > 0 ? 'With no category, everyone gets this role; set pay and times per person after.' : 'Everyone gets this role; set pay and times per person after. Categories in Settings fill those in too.'}>
              <SelectInput id="bulk-role" value={role} onChange={(event) => setRole(event.target.value as AssistantRole)}>
                {ASSISTANT_ROLES.map((key) => (
                  <option key={key} value={key}>
                    {ASSISTANT_ROLE_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
          )}
          <Field guide="assistants-bulk-people" label="People" htmlFor="bulk-people" required hint={tooMany ? `Up to ${MAX_BULK_ASSISTANTS} at a time.` : `${picked.length} picked. Type to search the team and the staff directory; anyone already on the event is left out. Someone new? Add them with Add assistant.`}>
            <MultiCombobox id="bulk-people" options={options} values={picked} onChange={setPicked} placeholder="Search by name" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Hired by" htmlFor="bulk-hired-by" hint="Who brought them on.">
              <SelectInput id="bulk-hired-by" value={hiredBy} onChange={(event) => setHiredBy(event.target.value as StaffSource | '')}>
                <option value="">Not said</option>
                {STAFF_SOURCES.map((key) => (
                  <option key={key} value={key}>
                    {STAFF_SOURCE_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Status" htmlFor="bulk-status" required hint="The same for everyone; change one at a time after.">
              <SelectInput id="bulk-status" value={status} onChange={(event) => setStatus(event.target.value as AssistantStatus)}>
                {ASSISTANT_STATUSES.map((key) => (
                  <option key={key} value={key}>
                    {key[0]!.toUpperCase() + key.slice(1)}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </div>
          {save.isError ? <ErrorNotice inline title="Not added" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button guide="assistants-bulk-submit" type="submit" disabled={!valid}>
              {save.isPending ? 'Adding…' : picked.length > 0 ? `Add ${picked.length} ${picked.length === 1 ? 'assistant' : 'assistants'}` : 'Add assistants'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the event's assistants cost, per category, for payroll: flat pay as
 * is, hourly pay times the hours between start and end. Internal roles only;
 * the API sends no totals to anyone else.
 */
function PayTotals({ totals, categoryNames }: { readonly totals: readonly CategoryPayTotal[]; readonly categoryNames: ReadonlyMap<string, string> }): React.ReactElement {
  const grand = totals.reduce((sum, row) => sum + row.total, 0);
  const people = totals.reduce((sum, row) => sum + row.people, 0);
  const unpriced = totals.reduce((sum, row) => sum + row.unpriced, 0);
  const line = (row: { readonly total: number; readonly people: number; readonly unpriced: number }): React.ReactNode => (
    <>
      <span className="block font-bold tabular-nums">{formatDollars(row.total)}</span>
      <span className="block text-portal-micro text-portal-muted">
        {row.people} {row.people === 1 ? 'person' : 'people'}
        {row.unpriced > 0 ? ` · ${row.unpriced} without pay or hours` : ''}
      </span>
    </>
  );
  return (
    <section data-guide="assistants-pay-totals" aria-label="Pay by category" className="mt-4 border-t border-portal-line pt-4">
      <FactList
        columns={4}
        facts={[
          ...totals.map((row) => ({ label: row.categoryId ? (categoryNames.get(row.categoryId) ?? 'Category') : 'No category', value: line(row) })),
          ...(totals.length > 1 ? [{ label: 'All assistants', value: line({ total: grand, people, unpriced }) }] : []),
        ]}
      />
    </section>
  );
}

function StaffingDialog({ eventId, existing, onClose, onSaved }: { readonly eventId: string; readonly existing: Staffing; readonly onClose: () => void; readonly onSaved: () => Promise<void> }): React.ReactElement {
  const [hiringMode, setHiringMode] = useState<'' | 'facilitator' | 'wingteam' | 'mixed'>(existing.hiringMode ?? '');
  const [requestedCount, setRequestedCount] = useState(existing.requestedCount !== null && existing.requestedCount !== undefined ? String(existing.requestedCount) : '');
  const [notes, setNotes] = useState(existing.notes ?? '');
  const save = useMutation({
    mutationFn: () => saveStaffing(eventId, { hiringMode: hiringMode || null, requestedCount: requestedCount.trim() === '' ? null : Number(requestedCount), notes: notes.trim() }),
    onSuccess: async () => {
      toast.success('Staffing plan saved');
      await onSaved();
    },
  });
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Staffing plan</DialogTitle>
        <form className="mt-2 space-y-4" onSubmit={(event) => { event.preventDefault(); if (!save.isPending) save.mutate(); }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="staffing-who" label="Who hires the assistants" htmlFor="staffing-mode">
              <SelectInput id="staffing-mode" value={hiringMode} onChange={(event) => setHiringMode(event.target.value as '' | 'facilitator' | 'wingteam' | 'mixed')}>
                <option value="">Not decided</option>
                <option value="facilitator">Facilitator</option>
                <option value="wingteam">WingTeam</option>
                <option value="mixed">Both</option>
              </SelectInput>
            </Field>
            <Field guide="staffing-count" label="How many" htmlFor="staffing-count">
              <TextInput id="staffing-count" type="number" min={0} value={requestedCount} onChange={(event) => setRequestedCount(event.target.value)} />
            </Field>
          </div>
          <Field guide="staffing-notes" label="Notes" htmlFor="staffing-notes">
            <TextArea id="staffing-notes" rows={3} value={notes} maxLength={2000} placeholder="Two need to be comfortable on stage; one drives the van." onChange={(event) => setNotes(event.target.value)} />
          </Field>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
